import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMoreNext] - The next page prefetch sentinel.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadMoreNextWrapper] - The "view more products" button wrapper.
 * @property {HTMLAnchorElement} [loadMoreNext] - The "view more products" button.
 * @property {HTMLElement} [loadMoreNextCount] - The range text of the next button.
 * @property {HTMLElement} [shopNextCategoryWrapper] - The "shop next category" CTA wrapper.
 * @property {HTMLElement} [loadMoreStatus] - Visually hidden live region announcing loads.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Map<number, Promise<void>>} */
  #pageFetches = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {IntersectionObserver | undefined} */
  #revealObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchNextPage();
    this.#observeViewMore();
    this.#observeReveal();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#revealObserver?.disconnect();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMoreNext } = this.refs;

    if (!viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting && entry.target === this.refs.viewMoreNext) {
              // Prefetch only — rendering is triggered by the "view more
              // products" button (see loadNextPage).
              this.#fetchNextPage();
            }
          }
        },
        {
          rootMargin: '400px',
        }
      );
    }

    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * Hides the load-more button until the user scrolls to ~2 rows from the end.
   * Sentinel = 2 rows of the current view's column count, minimum 8 products.
   * Fires once, then disconnects. Resets after each page load, filter change, or view switch.
   */
  #observeReveal() {
    const { loadMoreNextWrapper, grid } = this.refs;
    if (!loadMoreNextWrapper || !grid) return;

    // Signal to CSS that JS is controlling visibility
    loadMoreNextWrapper.dataset.jsReveal = '';

    this.#revealObserver?.disconnect();

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    const view = grid.getAttribute('product-grid-view') ?? 'default';
    const colMap = isMobile
      ? { default: 2, catalogue: 1, 'zoom-out': 4 }
      : { default: 4, catalogue: 3, 'zoom-out': 8 };
    const cols = colMap[view] ?? (isMobile ? 2 : 4);
    const SENTINEL_OFFSET = Math.max(cols * 2, 8);

    if (cards.length <= SENTINEL_OFFSET) {
      // Short collection — reveal immediately
      loadMoreNextWrapper.classList.add('load-more--revealed');
      return;
    }

    const sentinel = cards[cards.length - SENTINEL_OFFSET];

    this.#revealObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMoreNextWrapper.classList.add('load-more--revealed');
        this.#revealObserver?.disconnect();
      }
    });

    this.#revealObserver.observe(sentinel);
  }

  /**
   * Removes the revealed state and re-evaluates the scroll sentinel.
   * Call this after a view-mode switch so the button re-hides until the user
   * scrolls within 2 rows of the bottom of the new layout.
   */
  resetReveal() {
    this.refs.loadMoreNextWrapper?.classList.remove('load-more--revealed');
    this.#observeReveal();
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  async #fetchNextPage() {
    const page = this.#getNextPage();

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;

    // Dedupe concurrent fetches for the same page (sentinel prefetch + button click)
    let pendingFetch = this.#pageFetches.get(pageNumber);

    if (!pendingFetch) {
      pendingFetch = sectionRenderer
        .getSectionHTML(this.sectionId, true, pageInfo.url)
        .then((pageContent) => {
          this.pages.set(pageNumber, pageContent);
        })
        .finally(() => {
          this.#pageFetches.delete(pageNumber);
        });
      this.#pageFetches.set(pageNumber, pendingFetch);
    }

    await pendingFetch;
  }

  /**
   * Renders the next page when the "view more products" button is clicked.
   * Falls back to a regular navigation to the button's href without JS.
   *
   * @param {Event} event
   */
  async loadNextPage(event) {
    event.preventDefault();

    const button = event.target instanceof Element ? event.target.closest('a, button') : null;

    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    try {
      await this.#renderNextPage();
    } finally {
      button?.removeAttribute('aria-busy');
      this.#updateLoadMoreUi();
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getNextPage();

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchNextPage();

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchNextPage();
    });
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    const { cards } = this.refs;

    if (!Array.isArray(cards)) return;

    const targetCard = cards[cards.length - 1];

    if (!targetCard) return;

    const page = Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Updates the "view more products" button after a page render: visibility,
   * fallback href, range text ("25-48 of 57"), the next-category CTA swap,
   * and the live region.
   */
  #updateLoadMoreUi() {
    const { grid, loadMoreNextWrapper, loadMoreNext, loadMoreNextCount } = this.refs;
    const { shopNextCategoryWrapper, loadMoreStatus } = this.refs;

    if (!grid) return;

    // Read the cards from the DOM rather than this.refs — refs are refreshed by
    // a MutationObserver and can be one render behind right after an append.
    const cards = /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')));

    if (cards.length === 0) return;

    const pageSize = Number(grid.dataset.pageSize) || cards.length;
    const totalItems = Number(grid.dataset.totalItems) || cards.length;
    const lastPage = Number(grid.dataset.lastPage) || 1;
    const lastLoadedPage = Number(cards[cards.length - 1]?.dataset.page) || 1;

    // Both conditions must agree — the page counter and the item count can get
    // out of sync (e.g. mid filter re-render), and a "37-27 of 27" range must
    // never be shown.
    const hasNext = lastLoadedPage < lastPage && lastLoadedPage * pageSize < totalItems;

    if (loadMoreNextWrapper instanceof HTMLElement) {
      loadMoreNextWrapper.hidden = !hasNext;

      if (hasNext && loadMoreNext instanceof HTMLAnchorElement) {
        loadMoreNext.href = this.#getPageUrl(lastLoadedPage + 1);

        if (loadMoreNextCount instanceof HTMLElement) {
          const from = lastLoadedPage * pageSize + 1;
          const to = Math.min(from + pageSize - 1, totalItems);
          loadMoreNextCount.textContent = this.#formatTemplate(loadMoreNextWrapper.dataset.textRange, {
            from,
            to,
            total: totalItems,
          });
        }

        // New products appended — hide the button and wait for the user to
        // scroll near the bottom of the extended list before showing it again.
        loadMoreNextWrapper.classList.remove('load-more--revealed');
        this.#observeReveal();
      }
    }

    if (shopNextCategoryWrapper instanceof HTMLElement) {
      shopNextCategoryWrapper.hidden = hasNext;
    }

    if (loadMoreStatus instanceof HTMLElement) {
      loadMoreStatus.textContent = this.#formatTemplate(loadMoreStatus.dataset.textStatus, {
        count: cards.length,
        total: totalItems,
      });
    }
  }

  /**
   * @param {number} page
   * @returns {string}
   */
  #getPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';
    return url.toString();
  }

  /**
   * @param {string | undefined} template - Localised string with %key% placeholders
   * @param {Record<string, number>} values
   * @returns {string}
   */
  #formatTemplate(template, values) {
    if (!template) return '';
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replace(`%${key}%`, String(value)),
      template
    );
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#pageFetches.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolveNextPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#updateLoadMoreUi();

        // Reset reveal for the new filtered result set
        this.refs.loadMoreNextWrapper?.classList.remove('load-more--revealed');
        this.#observeReveal();

        // Fetch the next page
        this.#fetchNextPage();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
